import { useState } from 'react';
import type { FunctionComponent } from 'react';

import useRegenerateBackupCodes from '../../../../hooks/api/auth/mfa/useRegenerateBackupCodes';
import { BUTTON_TYPE } from '../../../generic';
import Button from '../../../generic/Button/Button';
import Flex from '../../../generic/Flex';
import Section from '../../../shared/settings/SettingsPageSection';
import type { MfaMethodListItem } from '../../../../../shared/types/routes/auth/mfa';

import BackupCodes from './BackupCodes';
import { getTitleAndDescriptionByType } from './index';
import MethodHeader from './MethodHeader';
import TotpSetupModal from './TotpSetupModal';

type Props = {
  method: MfaMethodListItem | undefined;
};

/**
 * TOTP/Authenticator App MFA method section.
 * Displays setup button or method details with backup codes.
 */
const TotpMethodSection: FunctionComponent<Props> = ({ method }) => {
  const [shouldShowSetupModal, setShouldShowSetupModal] = useState(false);
  const {
    onRegenerateBackupCodes,
    resetBackupCodes,
    isRegenerating,
    backupCodes: newBackupCodes,
  } = useRegenerateBackupCodes();

  const { title, description } = getTitleAndDescriptionByType('totp');

  return (
    <>
      <TotpSetupModal
        show={shouldShowSetupModal}
        onHide={() => setShouldShowSetupModal(false)}
        resetBackupCodes={resetBackupCodes}
      />

      <Section>
        <Section.Right>
          {!method ? (
            <Flex direction="column" gap="4px" justify="flex-start" align="flex-start">
              <strong>{title}</strong>
              <div>{description}</div>
              <Button
                type={BUTTON_TYPE.PRIMARY}
                onClick={() => setShouldShowSetupModal(true)}
              >
                Enable
              </Button>
            </Flex>
          ) : (
            <div>
              <MethodHeader method={method} />

              <div className="text-muted">
                {method.unusedBackupCodeCount} unused backup {method.unusedBackupCodeCount === 1 ? 'code' : 'codes'} remaining
              </div>

              <hr />

              <div className="margin-top-lg">
                <strong>Backup Codes</strong>
                <BackupCodes codes={newBackupCodes} />
                <div className="margin-top-lg">
                  <Button
                    type={BUTTON_TYPE.SECONDARY}
                    onClick={() => onRegenerateBackupCodes()}
                    isDisabled={isRegenerating}
                  >
                    {isRegenerating ? 'Regenerating...' : 'Regenerate Backup Codes'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </Section.Right>
      </Section>
    </>
  );
};

export default TotpMethodSection;
